import { coercePref } from './coerce-pref.util';

/**
 * The user's colour-vision preference.
 *
 * THERE IS NO 'system' VALUE, AND THAT IS THE ONE STRUCTURAL DIFFERENCE FROM EVERY OTHER PRESET
 * IN THIS FAMILY. `display.theme` follows `prefers-color-scheme` and `display.contrast` follows
 * `prefers-contrast`, because an operating system knows those things about its user. No platform
 * exposes a colour-vision signal — none of `prefers-color-scheme`, `prefers-contrast`,
 * `forced-colors` or any media feature in CSS Media Queries 5 says anything about cone response,
 * and a browser that did expose one would be leaking a medical fact the user never typed. So this
 * preference is asked for and never inferred, and `AttributePresetStore` is given no media query
 * at all rather than one that can never match.
 *
 * THE VOCABULARY IS THE LIVE ONE, NOT THE FULL ONE. `CVD_DEFICIENCIES` in
 * scripts/lib/theme-blocks.js names three deficiencies; `LIVE_CVD_DEFICIENCIES` names the ones
 * whose blocks _tokens.scss actually carries. The two lists name the same three today — protan
 * (MR !168), deutan (MR !172) and tritan (MR !173) — and the distinction is NOT retired by that:
 * it is the reason each of the three could only be offered on the day its blocks landed. Offering
 * a user "tritanopia" while no tritan block existed would have stamped `data-cvd='tritan'`, matched
 * nothing, and painted the ordinary theme — a setting that reports success and does nothing, which
 * is worse than an absent option. `verify-theme-axes.js` FAILS when this list stops matching the
 * live registry, so the day a deficiency lands, the day it is offered here and the day the gate
 * goes green are the same day — which is how all three arrived, and is the only way a fourth may.
 */
export type CvdPreset = 'none' | 'protan' | 'deutan' | 'tritan';

/** What gets stamped. `null` is the attribute's ABSENCE — ordinary colour vision, the default,
 *  and the state every non-cvd block is written for. */
export type ResolvedCvd = 'protan' | 'deutan' | 'tritan' | null;

/** Key in the /v2/userSettings blob. Frontend-owned and opaque to the backend. */
export const CVD_KEY = 'display.cvd';

/** **sessionStorage** key holding the RESOLVED value, read by the inline script in index.html.
 *  Two states only — a deficiency name, or absent. Unlike the contrast mirror there is no
 *  PINNED_OFF sentinel to distinguish "the user pinned off" from "nobody has said anything",
 *  because with no OS signal to consult the two are the same instruction: stamp nothing.
 *
 *  THE AREA IS PART OF THE DECISION, not an implementation detail: the other two mirrors are
 *  localStorage and outlive the tab, and this one must not — see
 *  `AttributePresetConfig.mirrorArea` for the disclosure that buys, and `CvdService`'s
 *  `identityScoped()` for the half of it that covers a logout and a cross-tab switch. */
export const CVD_STORAGE_KEY = 'g15.cvd';

export const CVD_PRESETS: readonly CvdPreset[] = ['none', 'protan', 'deutan', 'tritan'] as const;

/** Coerce an untrusted value (a settings-blob entry or a cross-tab broadcast payload) to a known
 *  preset — including a deficiency that is real but NOT LIVE, which coerces to 'none' rather than
 *  being stamped: a blob written by a future build, read by an older one, must not paint a block
 *  this bundle does not carry. */
export function coerceCvd(v: unknown): CvdPreset {
  return coercePref(v, CVD_PRESETS, 'none');
}

/**
 * Map a stored preset to what is painted. A pure function of its argument, and it takes no media
 * flag at all — see the type's own note on why there is nothing to consult.
 */
export function resolveCvd(preset: CvdPreset): ResolvedCvd {
  return preset === 'none' ? null : preset;
}
